"use client";

import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { FiCopy, FiCheck } from "react-icons/fi";
import { useRef, useCallback, useState } from "react";
import BackgroundImage from "../../../../../../components/ui/BackgroundImage";
import referralBarBg from "../../assets/images/referral-bar-bg.webp";
import referralIcon from "../../assets/images/referral-icon.webp";
import CardHeading from "../content-grid/CardHeading";
import clickSound from "../../../../../../assets/audio/click.wav";
import { useAudioEffects } from "../../../../../../hooks/use-audio-effects";

// ─── Internal Types ───────────────────────────────────────────────────────────

interface Particle {
  id: number;
  angle: number;
  distance: number;
  size: number;
  color: string;
  duration: number;
  shape: "circle" | "spark";
  gravityY: number;
}

interface RippleRing {
  id: number;
  delay: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const GOLD_PALETTE = ["#FFCA58", "#FFE29A", "#FFB800", "#FFF3C0", "#FF9D00"];

// ─── Animation Variants ───────────────────────────────────────────────────────
// All variants defined outside component — zero re-creation on re-render.

const bannerVariants: any = {
  hidden: { opacity: 0, y: 32, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.12,
      delayChildren: 0.05,
    },
  },
};

const slideFromLeft: any = {
  hidden: { opacity: 0, x: -28 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const slideFromRight: any = {
  hidden: { opacity: 0, x: 28 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const wordReveal: any = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const iconSwap: any = {
  enter: { opacity: 0, scale: 0.3, rotate: -120 },
  center: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: { type: "spring", stiffness: 700, damping: 22 },
  },
  exit: { opacity: 0, scale: 0.3, rotate: 120, transition: { duration: 0.12 } },
};

const wordStagger: any = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.3 } },
};

// ─── Props Interface ───────────────────────────────────────────────────────────

export interface ReferralBannerProps {
  /** Heading text displayed in the banner. @default "REFERAL" */
  title?: string;
  /** Label for the action button. @default "ACCESEAZA" */
  actionCtaText?: string;
  /** Fired when the action button is clicked. */
  onAction?: () => void;
  /** The referral code to display and copy. */
  code: string;
  /** Label rendered above the code. @default "YOUR CODE:" */
  codeLabel?: string;

  /**
   * Controls the copy icon — `true` shows a checkmark.
   * Reset this in the parent after ~2s.
   * @default false
   */
  copied?: boolean;
  /**
   * Fired on copy button click.
   * Receives the code string — use it to write to clipboard, show a toast, etc.
   */
  onCopy?: (code: string) => void;
  /** Extra Tailwind classes appended to the root element. */
  className?: string;
}

// ─── ReferralBar ──────────────────────────────────────────────────────────────

export default function ReferralBar({
  title = "REFERAL",
  actionCtaText = "ACCESEAZA",
  onAction,
  code,
  codeLabel = "YOUR CODE:",
  copied = false,
  onCopy,

  className = "",
}: ReferralBannerProps) {
  const shouldReduceMotion = useReducedMotion();

  const [particles, setParticles] = useState<Particle[]>([]);
  const [ripples, setRipples] = useState<RippleRing[]>([]);
  const [showFlash, setShowFlash] = useState(false);
  const [showCopiedLabel, setShowCopiedLabel] = useState(false);

  const particleCounter = useRef(0);
  const rippleCounter = useRef(0);

  const { playEffect } = useAudioEffects();

  /**
   * Triggers the full 7-layer copy explosion:
   * flash → ripple rings → multi-ring particles → button shake →
   * code box glow → border flash → char stagger
   */
  const spawnExplosion = useCallback(() => {
    if (shouldReduceMotion) return;

    // 1 ─ Banner radial flash
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 380);

    // 2 ─ Floating "COPIED!" label
    setShowCopiedLabel(true);
    setTimeout(() => setShowCopiedLabel(false), 1300);

    // 3 ─ Three staggered ripple rings from button centre
    const newRipples: RippleRing[] = [
      { id: ++rippleCounter.current, delay: 0 },
      { id: ++rippleCounter.current, delay: 0.1 },
      { id: ++rippleCounter.current, delay: 0.22 },
    ];
    setRipples((prev) => [...prev, ...newRipples]);
    setTimeout(
      () => setRipples((prev) => prev.filter((r) => !newRipples.includes(r))),
      1000,
    );

    // 4 ─ Three rings of particles (inner circles / mid sparks / outer long sparks)
    const ring1: Particle[] = Array.from({ length: 14 }, (_, i) => ({
      id: ++particleCounter.current,
      angle: (i / 14) * Math.PI * 2 + Math.random() * 0.25,
      distance: 36 + Math.random() * 12,
      size: 4 + Math.random() * 3,
      color: GOLD_PALETTE[Math.floor(Math.random() * GOLD_PALETTE.length)],
      duration: 0.42 + Math.random() * 0.1,
      shape: "circle",
      gravityY: 0,
    }));

    const ring2: Particle[] = Array.from({ length: 9 }, (_, i) => ({
      id: ++particleCounter.current,
      angle: (i / 9) * Math.PI * 2 + Math.PI / 9,
      distance: 58 + Math.random() * 18,
      size: 5,
      color: GOLD_PALETTE[Math.floor(Math.random() * GOLD_PALETTE.length)],
      duration: 0.58 + Math.random() * 0.12,
      shape: "spark",
      gravityY: 10,
    }));

    const ring3: Particle[] = Array.from({ length: 6 }, (_, i) => ({
      id: ++particleCounter.current,
      angle: (i / 6) * Math.PI * 2 + Math.random() * 0.4,
      distance: 78 + Math.random() * 14,
      size: 9,
      color: "#FFE29A",
      duration: 0.72 + Math.random() * 0.18,
      shape: "spark",
      gravityY: 18,
    }));

    const allParticles = [...ring1, ...ring2, ...ring3];
    setParticles((prev) => [...prev, ...allParticles]);
    setTimeout(
      () =>
        setParticles((prev) => prev.filter((p) => !allParticles.includes(p))),
      1100,
    );
  }, [shouldReduceMotion]);

  const handleCopy = useCallback(() => {
    playEffect(clickSound);
    navigator.clipboard?.writeText(code).catch(() => {});
    spawnExplosion();
    onCopy?.(code);
  }, [code, onCopy, spawnExplosion]);

  const handleSubmit = useCallback(() => {
    onAction?.();
  }, [onAction]);

  return (
    <motion.div variants={bannerVariants} initial="hidden" animate="visible">
      <BackgroundImage imageSrc={referralBarBg} imageClassName="!object-fill">
        <div
          className={[
            "relative flex items-center justify-between gap-6 overflow-hidden",
            "px-10 py-5",
            "chakra-petch-font", // FONT
            className,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <DiagonalStripes />

          {/* 5 ── Full-banner radial gold flash on copy ──────────────────── */}
          <AnimatePresence>
            {showFlash && (
              <motion.span
                className="pointer-events-none absolute inset-0 z-20"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.22, 0] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.38, ease: "easeOut" }}
                style={{
                  background:
                    "radial-gradient(ellipse at 82% 50%, rgba(255,202,88,0.55) 0%, transparent 68%)",
                }}
              />
            )}
          </AnimatePresence>

          {/* ── Left — Icon + Title ──────────────────────────────────────── */}
          <motion.div
            variants={slideFromLeft}
            className="relative z-10 flex items-center gap-4 min-w-0"
          >
            <img
              loading="lazy"
              alt="icon"
              src={referralIcon}
              className="w-full h-full max-w-[104px]"
            />
            <CardHeading
              label={title}
              ctaText={actionCtaText}
              cardButtonColors={{
                from: "#414447",
                via: "#323536",
                to: "transparent",
              }}
              ctaClassName="!mt-1"
              onCtaClick={handleSubmit}
            />
          </motion.div>

          {/* ── Right — Code ─────────────────────────────────────────────── */}
          <motion.div
            variants={slideFromRight}
            className="relative z-10 flex flex-col items-end gap-[6px] flex-shrink-0"
          >
            {/* Code label words stagger in */}
            <motion.span
              variants={wordStagger}
              initial="hidden"
              animate="visible"
              className="text-[.85rem]  gap-1 flex flex-wrap"
            >
              {codeLabel?.split(" ")?.map((word, i, arr) => (
                <motion.span
                  key={i}
                  variants={wordReveal}
                  className={
                    i === arr.length - 1 ? "text-white" : "text-white/50"
                  }
                >
                  {word}
                </motion.span>
              ))}
            </motion.span>

            <div className="flex items-center gap-2">
              {/* ── Copy button ──────────────────────────────────────────── */}
              <div className="relative flex items-center justify-center">
                {/* 3 ── Expanding ripple rings ─────────────────────────── */}
                {ripples.map((r) => (
                  <motion.span
                    key={r.id}
                    className="pointer-events-none absolute rounded-full border-2 border-[#FFCA58]"
                    initial={{ width: 18, height: 18, opacity: 0.95 }}
                    animate={{
                      width: 96,
                      height: 96,
                      opacity: 0,
                      borderWidth: 0,
                    }}
                    transition={{
                      duration: 0.72,
                      delay: r.delay,
                      ease: "easeOut",
                    }}
                    style={{
                      top: "50%",
                      left: "50%",
                      translateX: "-50%",
                      translateY: "-50%",
                    }}
                  />
                ))}

                {/* 4 ── Multi-ring particle burst ──────────────────────── */}
                {particles.map((p) => (
                  <motion.span
                    key={p.id}
                    className="pointer-events-none absolute"
                    style={{
                      top: "50%",
                      left: "50%",
                      width: p.shape === "spark" ? p.size / 2.2 : p.size,
                      height: p.size,
                      borderRadius: p.shape === "circle" ? "50%" : "2px",
                      backgroundColor: p.color,
                    }}
                    initial={{
                      opacity: 1,
                      scale: 1,
                      x: 0,
                      y: 0,
                      translateX: "-50%",
                      translateY: "-50%",
                      rotate:
                        p.shape === "spark" ? (p.angle * 180) / Math.PI : 0,
                    }}
                    animate={{
                      opacity: 0,
                      scale: 0,
                      x: Math.cos(p.angle) * p.distance,
                      y: Math.sin(p.angle) * p.distance + p.gravityY,
                    }}
                    transition={{
                      duration: p.duration,
                      ease: [0.15, 0, 0.75, 1],
                    }}
                  />
                ))}

                {/* The button itself */}
                <motion.button
                  type="button"
                  onClick={handleCopy}
                  aria-label="Copy referral code"
                  title="Copy referral code"
                  className="rounded-md bg-[#FFCA58]/20 p-3 border-[1px] border-dashed border-[#FFCA58]/35"
                  // 6 ── Button celebration shake + mega glow on copy ────
                  animate={
                    shouldReduceMotion
                      ? {}
                      : copied
                        ? {
                            scale: [1, 0.68, 1.42, 0.92, 1.12, 1],
                            rotate: [0, -14, 12, -7, 4, 0],
                            boxShadow: [
                              "0 0 0px 0px rgba(255,202,88,0)",
                              "0 0 42px 14px rgba(255,202,88,0.75)",
                              "0 0 24px 6px rgba(255,202,88,0.4)",
                              "0 0 14px 3px rgba(255,202,88,0.2)",
                              "0 0 8px 1px rgba(255,202,88,0.1)",
                            ],
                          }
                        : {}
                  }
                  transition={
                    copied
                      ? {
                          duration: 0.58,
                          times: [0, 0.1, 0.26, 0.42, 0.7, 1],
                          ease: "easeOut",
                        }
                      : {}
                  }
                  whileHover={
                    !copied
                      ? {
                          scale: 1.14,
                          backgroundColor: "rgba(255,202,88,0.30)",
                          boxShadow: "0 0 18px 5px rgba(255,202,88,0.4)",
                        }
                      : {}
                  }
                  whileTap={{ scale: 0.82, rotate: -10 }}
                >
                  <AnimatePresence mode="wait">
                    {copied ? (
                      <motion.span
                        key="check"
                        variants={iconSwap}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        style={{ display: "block" }}
                      >
                        <FiCheck
                          className="w-[16px] h-[16px] text-green-400"
                          style={{ strokeWidth: 3 }}
                        />
                      </motion.span>
                    ) : (
                      <motion.span
                        key="copy"
                        variants={iconSwap}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        style={{ display: "block" }}
                      >
                        <FiCopy
                          className="w-[16px] h-[16px] text-[#FFCA58]"
                          style={{ strokeWidth: 2 }}
                        />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>

              {/* ── Code display ─────────────────────────────────────────── */}
              <div className="relative">
                {/* 2 ── Floating "✦ COPIED! ✦" label ──────────────────── */}
                <AnimatePresence>
                  {showCopiedLabel && (
                    <motion.span
                      className="pointer-events-none absolute -top-10 left-[30%] z-30
                                 text-[#FFCA58] text-[0.7rem] font-black chakra-petch-font
                                 tracking-[0.25em] whitespace-nowrap"
                      initial={{ opacity: 0, y: 4, scale: 0.65 }}
                      animate={{ opacity: 1, y: -6, scale: 1 }}
                      exit={{ opacity: 0, y: -22, scale: 0.75 }}
                      transition={{
                        type: "spring",
                        stiffness: 520,
                        damping: 18,
                      }}
                    >
                      ✦ COPIED! ✦
                    </motion.span>
                  )}
                </AnimatePresence>

                {/* 5+7 ── Code box glow + border flash ────────────────── */}
                <motion.div
                  className="flex items-center justify-center min-w-[240px] min-h-[50px] rounded-md
                             bg-[#C9C9C9]/[0.07] border-[1px] border-dashed border-white/10
                             outline-none overflow-hidden relative"
                  animate={
                    shouldReduceMotion
                      ? {}
                      : copied
                        ? {
                            boxShadow: [
                              "0 0 0px 0px rgba(255,202,88,0)",
                              "0 0 36px 10px rgba(255,202,88,0.55)",
                              "0 0 18px 4px rgba(255,202,88,0.22)",
                              "0 0 6px 1px rgba(255,202,88,0.07)",
                            ],
                            scale: [1, 1.03, 1.015, 1],
                            borderColor: [
                              "rgba(255,255,255,0.1)",
                              "rgba(255,202,88,0.9)",
                              "rgba(255,202,88,0.4)",
                              "rgba(255,255,255,0.1)",
                            ],
                          }
                        : {}
                  }
                  transition={
                    copied
                      ? {
                          duration: 0.85,
                          times: [0, 0.14, 0.5, 1],
                          ease: "easeOut",
                        }
                      : {}
                  }
                >
                  {/* Shimmer sweep */}
                  <AnimatePresence>
                    {copied && (
                      <motion.span
                        key="shimmer"
                        className="pointer-events-none absolute inset-0 z-10"
                        initial={{ x: "-110%" }}
                        animate={{ x: "210%" }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                        style={{
                          background:
                            "linear-gradient(90deg, transparent 0%, rgba(255,202,88,0.45) 50%, transparent 100%)",
                        }}
                      />
                    )}
                  </AnimatePresence>

                  {/* 7 ── Per-character stagger bounce + gold flash ────── */}
                  <div className="relative z-20 flex items-center justify-center">
                    {code.split("").map((char, i) => (
                      <motion.span
                        key={i}
                        className="text-[1.5rem] chakra-petch-font text-center"
                        animate={
                          copied
                            ? {
                                color: [
                                  "rgba(255,255,255,0.4)",
                                  "#FFCA58",
                                  "rgba(255,255,255,0.95)",
                                  "rgba(255,255,255,0.4)",
                                ],
                                y: [0, -9, 2, 0],
                                scale: [1, 1.25, 0.95, 1],
                              }
                            : {
                                color: "rgba(255,255,255,0.4)",
                                y: 0,
                                scale: 1,
                              }
                        }
                        transition={
                          copied
                            ? {
                                duration: 0.48,
                                delay: i * 0.038,
                                ease: "easeOut",
                              }
                            : { duration: 0.3 }
                        }
                      >
                        {char}
                      </motion.span>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </BackgroundImage>
    </motion.div>
  );
}

// ─── Diagonal Stripes Decoration ─────────────────────────────────────────────

function DiagonalStripes() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-y-0 left-[30%] right-[20%]"
      style={{
        opacity: 0.08,
        backgroundImage:
          "repeating-linear-gradient(135deg, #c8a020 0px, #c8a020 2px, transparent 2px, transparent 16px)",
      }}
    />
  );
}
